/**
 * Painel de administração.
 *
 * Ordem das seções não é estética: é a ordem em que alguém precisa olhar
 * numa madrugada de incidente. Alertas primeiro, porque é o único bloco
 * que responde "preciso agir agora?". Depois os saques, que é dinheiro
 * esperando uma decisão de pessoa. Depois as partidas abertas, que é onde
 * há dinheiro preso. Faturamento e sinais de fraude vêm por último —
 * importam, mas não são urgência.
 *
 * Todo botão que escreve aqui mexe em dinheiro de alguém, e nenhum age
 * sem motivo escrito. Aprovar saque é a decisão mais séria da tela: é o
 * momento em que dinheiro sai da plataforma e não volta. Por isso a linha
 * do saque carrega os três números — depositado, apostado, sacado — em
 * vez de só o valor pedido. Aprovar sabendo apenas quanto a pessoa quer
 * tirar é assinar em papel em branco; a distância entre o que ela
 * depositou e o que arriscou em mesa é o que denuncia conta usada como
 * caixa.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  type Alertas,
  type FilaDeSaques,
  type Painel,
  type PartidaAberta,
  type SaquePendente,
  cancelarPartida,
  carregarPainel,
  carregarSaques,
  decidirIdentidade,
  decidirSaque,
} from '../lib/admin.ts';
import { money } from '../lib/format.ts';

const JOGO: Record<string, string> = {
  CHESS: 'Xadrez',
  CHECKERS: 'Damas',
  TIC_TAC_TOE: 'Jogo da velha',
};

/** Cada alerta, com o rótulo e o quanto um número diferente de zero pesa. */
const ALERTAS: readonly {
  chave: keyof Alertas;
  rotulo: string;
  gravidade: 'critico' | 'atencao';
  explica: string;
}[] = [
  {
    chave: 'carteiras_com_drift',
    rotulo: 'Carteiras com drift',
    gravidade: 'critico',
    explica: 'Cache e ledger discordam. Congele saques e investigue antes de qualquer coisa.',
  },
  {
    chave: 'depositos_divergentes',
    rotulo: 'Depósitos divergentes',
    gravidade: 'critico',
    explica: 'Valor pago diferente do cobrado.',
  },
  {
    chave: 'partidas_penduradas',
    rotulo: 'Partidas penduradas',
    gravidade: 'atencao',
    explica: 'Mais de 2h em andamento: motor caído com dinheiro preso no escrow.',
  },
  {
    chave: 'saques_aguardando_aprovacao',
    rotulo: 'Saques a aprovar',
    gravidade: 'atencao',
    explica: 'Pedidos esperando decisão humana.',
  },
  {
    chave: 'webhooks_falhados',
    rotulo: 'Webhooks falhados',
    gravidade: 'atencao',
    explica: 'Eventos do PSP que não processaram.',
  },
  {
    chave: 'assinaturas_invalidas_1h',
    rotulo: 'Assinaturas inválidas (1h)',
    gravidade: 'atencao',
    explica: 'Webhook chegando com assinatura errada.',
  },
  {
    chave: 'partidas_em_disputa',
    rotulo: 'Partidas em disputa',
    gravidade: 'atencao',
    explica: 'Aguardando arbitragem.',
  },
];

function idade(segundos: number): string {
  if (segundos < 60) return `${segundos}s`;
  if (segundos < 3600) return `${Math.floor(segundos / 60)}min`;
  if (segundos < 86400) return `${Math.floor(segundos / 3600)}h`;
  return `${Math.floor(segundos / 86400)}d`;
}

/** Identificador curto: a tabela fica ilegível com uuid inteiro. */
function curto(id: string): string {
  return id.slice(0, 8);
}

function Painelzinho({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="stat">
      <div className="rotulo">{rotulo}</div>
      <div className="stat-value">{valor}</div>
    </div>
  );
}

/** Linha de partida aberta, com o cancelamento em duas etapas. */
function LinhaPartida({
  partida,
  onCancelar,
  ocupado,
}: {
  partida: PartidaAberta;
  onCancelar: (matchId: string, motivo: string) => Promise<void>;
  ocupado: boolean;
}) {
  const [abrindo, setAbrindo] = useState(false);
  const [motivo, setMotivo] = useState('');

  const pendurada = partida.idadeSegundos > 7200;

  return (
    <li className="card p-4 grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-semibold">
          {JOGO[partida.gameType] ?? partida.gameType}
          <span className="ml-2 selo">{partida.stakeMode === 'REAL' ? 'real' : 'virtual'}</span>
        </span>
        <code className="text-xs text-muted-foreground">{curto(partida.id)}</code>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <div>
          <dt className="rotulo">Estado</dt>
          <dd>{partida.status}</dd>
        </div>
        <div>
          <dt className="rotulo">Idade</dt>
          <dd style={pendurada ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
            {idade(partida.idadeSegundos)}
          </dd>
        </div>
        <div>
          <dt className="rotulo">Entrada</dt>
          <dd className="money">
            {partida.stakeAgreed ? money(partida.entryFeeCents) : 'a combinar'}
          </dd>
        </div>
        <div>
          <dt className="rotulo">Escrows presos</dt>
          <dd>{partida.escrowsPresos}</dd>
        </div>
      </dl>

      <p className="text-xs text-muted-foreground">
        Jogadores: {partida.jogadores.map(curto).join(', ')}
      </p>

      {abrindo ? (
        <div className="grid gap-2 border-t pt-3">
          <label className="grid gap-1.5">
            <span className="rotulo">Motivo do cancelamento</span>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="ex.: motor caiu com a partida presa"
              className="w-full rounded-lg border px-3 py-2 outline-none transition"
              style={{ background: 'var(--card)' }}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            Devolve {partida.escrowsPresos > 0 ? 'o escrow integral, sem taxa' : 'nada, porque não há escrow preso'}, e
            fica registrado no seu nome.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={ocupado || motivo.trim() === ''}
              onClick={() => {
                void onCancelar(partida.id, motivo.trim()).then(() => {
                  setAbrindo(false);
                  setMotivo('');
                });
              }}
            >
              Confirmar cancelamento
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setAbrindo(false)}>
              Voltar
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-ghost justify-self-start"
          disabled={ocupado}
          onClick={() => setAbrindo(true)}
        >
          Cancelar partida
        </button>
      )}
    </li>
  );
}

/** Quanto do que entrou passou por uma mesa. */
function proporcaoApostada(historico: SaquePendente['historico']): string {
  if (historico.depositadoCents === 0) return 'sem depósito';
  const razao = historico.apostadoCents / historico.depositadoCents;
  return `${Math.round(razao * 100)}% do depositado`;
}

/**
 * O que sustenta o "verificado" da conta.
 *
 * As duas afirmacoes nao valem o mesmo: a consulta a Receita diz que o
 * CPF existe e que o nascimento confere; a conferencia documental diz
 * que alguem olhou um documento com foto. Quem aprova saque decide
 * diferente sabendo qual foi.
 */
function origemDaVerificacao(metodo: SaquePendente['kycMethod']): string {
  if (metodo === 'RECEITA') return 'conferida na base da Receita.';
  if (metodo === 'DOCUMENTO') return 'conferida por documento, por uma pessoa da equipe.';
  // Contas verificadas antes de a coluna existir. Nao da para afirmar
  // qual dos dois caminhos foi, e inventar seria pior que admitir.
  return 'verificada antes de o método passar a ser registrado.';
}

function LinhaSaque({
  saque,
  onDecidir,
  onIdentidade,
  ocupado,
}: {
  saque: SaquePendente;
  onDecidir: (id: string, aprovar: boolean, motivo: string) => Promise<void>;
  onIdentidade: (userId: string, motivo: string) => Promise<void>;
  ocupado: boolean;
}) {
  const [motivo, setMotivo] = useState('');
  const [confirmando, setConfirmando] = useState<'aprovar' | 'recusar' | null>(null);

  const kycPendente = saque.kycStatus !== 'VERIFIED';
  // Pouco jogo e muito depósito é o desenho de quem está passando dinheiro
  // pela plataforma, não jogando nela.
  const poucoJogo =
    saque.historico.depositadoCents > 0 &&
    saque.historico.apostadoCents < saque.historico.depositadoCents / 2;

  return (
    <li className="card p-4 grid gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="font-semibold">
          {saque.displayName ?? curto(saque.userId)}
          {saque.ambiente === 'HOMOLOGACAO' ? <span className="ml-2 selo">teste</span> : null}
        </span>
        <span className="money font-bold" style={{ fontSize: 'var(--text-title)' }}>
          {money(saque.amountCents)}
        </span>
      </div>

      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
        <div>
          <dt className="rotulo">Estado</dt>
          <dd>{saque.status}</dd>
        </div>
        <div>
          <dt className="rotulo">Depositou</dt>
          <dd className="money">{money(saque.historico.depositadoCents)}</dd>
        </div>
        <div>
          <dt className="rotulo">Apostou</dt>
          <dd
            className="money"
            style={poucoJogo ? { color: 'var(--danger)', fontWeight: 600 } : undefined}
          >
            {money(saque.historico.apostadoCents)}
          </dd>
        </div>
        <div>
          <dt className="rotulo">Já sacou</dt>
          <dd className="money">{money(saque.historico.sacadoCents)}</dd>
        </div>
      </dl>

      <p className="text-xs text-muted-foreground">
        Arriscou {proporcaoApostada(saque.historico)} em mesa.
        {poucoJogo
          ? ' Depositou bem mais do que jogou — vale olhar antes de liberar.'
          : ''}
      </p>

      {kycPendente ? null : (
        <p className="text-xs text-muted-foreground">
          Identidade {origemDaVerificacao(saque.kycMethod)}
        </p>
      )}

      {kycPendente ? (
        <div className="grid gap-2 border-t pt-3">
          <p className="text-sm" style={{ color: 'var(--danger)' }}>
            Identidade em {saque.kycStatus}. O saque não sai enquanto ela não for verificada.
          </p>
          <label className="grid gap-1.5">
            <span className="rotulo">O que você conferiu</span>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="ex.: documento com foto confere com o CPF declarado"
              className="w-full rounded-lg border px-3 py-2 outline-none transition"
              style={{ background: 'var(--card)' }}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            Isto não verifica nada sozinho: registra que você verificou, com seu nome e a
            hora. É o que destrava o saque desta conta.
          </p>
          <button
            type="button"
            className="btn btn-primary justify-self-start"
            disabled={ocupado || motivo.trim() === ''}
            onClick={() => {
              void onIdentidade(saque.userId, motivo.trim()).then(() => setMotivo(''));
            }}
          >
            Registrar identidade verificada
          </button>
        </div>
      ) : saque.status !== 'REQUESTED' ? (
        <p className="text-xs text-muted-foreground border-t pt-3">
          Já aprovado. O envio ao banco acontece sozinho, e a conciliação encerra quando o
          banco confirmar.
        </p>
      ) : confirmando ? (
        <div className="grid gap-2 border-t pt-3">
          <label className="grid gap-1.5">
            <span className="rotulo">
              {confirmando === 'aprovar' ? 'Motivo da aprovação' : 'Motivo da recusa'}
            </span>
            <input
              type="text"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={
                confirmando === 'aprovar'
                  ? 'ex.: histórico de jogo normal, identidade conferida'
                  : 'ex.: depósito sem jogo, pedido de esclarecimento enviado'
              }
              className="w-full rounded-lg border px-3 py-2 outline-none transition"
              style={{ background: 'var(--card)' }}
            />
          </label>
          <p className="text-xs text-muted-foreground">
            {confirmando === 'aprovar'
              ? `Libera ${money(saque.amountCents)} para o CPF desta conta. Depois que o banco confirmar, não volta.`
              : 'Devolve o valor à carteira da pessoa, por transação de estorno.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-primary"
              disabled={ocupado || motivo.trim() === ''}
              onClick={() => {
                void onDecidir(saque.id, confirmando === 'aprovar', motivo.trim()).then(() => {
                  setConfirmando(null);
                  setMotivo('');
                });
              }}
            >
              {confirmando === 'aprovar' ? 'Confirmar aprovação' : 'Confirmar recusa'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setConfirmando(null)}>
              Voltar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 border-t pt-3">
          <button
            type="button"
            className="btn btn-primary"
            disabled={ocupado}
            onClick={() => setConfirmando('aprovar')}
          >
            Aprovar
          </button>
          <button
            type="button"
            className="btn btn-ghost"
            disabled={ocupado}
            onClick={() => setConfirmando('recusar')}
          >
            Recusar
          </button>
        </div>
      )}
    </li>
  );
}

export function Admin({ token, onSair }: { token: string; onSair: () => void }) {
  const [painel, setPainel] = useState<Painel | null>(null);
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'sem-acesso' | 'erro'>('carregando');
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [fila, setFila] = useState<FilaDeSaques | null>(null);

  const recarregar = useCallback(async (): Promise<void> => {
    const saques = await carregarSaques(token);
    if (saques.estado === 'ok') setFila(saques.fila);

    const resultado = await carregarPainel(token);
    if (resultado.estado === 'ok') {
      setPainel(resultado.painel);
      setEstado('ok');
      return;
    }
    if (resultado.estado === 'sem-acesso') {
      setEstado('sem-acesso');
      return;
    }
    setEstado('erro');
    setMensagem(resultado.mensagem);
  }, [token]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  async function decidir(id: string, aprovar: boolean, motivo: string): Promise<void> {
    setOcupado(true);
    setMensagem(null);
    const resultado = await decidirSaque(token, id, aprovar, motivo);
    setMensagem(
      resultado.estado === 'erro'
        ? resultado.mensagem
        : aprovar
          ? 'Saque aprovado. O envio ao banco acontece na próxima varredura.'
          : 'Saque recusado e valor devolvido à carteira.',
    );
    await recarregar();
    setOcupado(false);
  }

  async function verificarIdentidade(userId: string, motivo: string): Promise<void> {
    setOcupado(true);
    setMensagem(null);
    const resultado = await decidirIdentidade(token, userId, true, motivo);
    setMensagem(
      resultado.estado === 'erro' ? resultado.mensagem : 'Identidade registrada como verificada.',
    );
    await recarregar();
    setOcupado(false);
  }

  async function cancelar(matchId: string, motivo: string): Promise<void> {
    setOcupado(true);
    setMensagem(null);
    const resultado = await cancelarPartida(token, matchId, motivo);
    if (resultado.estado === 'erro') setMensagem(resultado.mensagem);
    else setMensagem('Partida cancelada e escrow devolvido.');
    await recarregar();
    setOcupado(false);
  }

  if (estado === 'carregando') {
    return (
      <main className="grow grid place-items-center text-muted-foreground">Abrindo o painel…</main>
    );
  }

  if (estado === 'sem-acesso') {
    return (
      <main className="grow grid place-items-center px-4">
        <div className="card p-6 max-w-md w-full grid gap-4 text-center">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            Sem acesso
          </h1>
          <p className="text-muted-foreground">
            Esta conta não está na lista de administradores.
          </p>
          <button type="button" className="btn btn-ghost" onClick={onSair}>
            Voltar ao lobby
          </button>
        </div>
      </main>
    );
  }

  if (estado === 'erro' || !painel) {
    return (
      <main className="grow grid place-items-center px-4">
        <div className="card p-6 max-w-md w-full grid gap-4 text-center">
          <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
            Painel indisponível
          </h1>
          <p className="text-muted-foreground">{mensagem ?? 'Não deu para carregar.'}</p>
          <button type="button" className="btn btn-primary" onClick={() => void recarregar()}>
            Tentar de novo
          </button>
        </div>
      </main>
    );
  }

  const criticos = ALERTAS.filter(
    (a) => a.gravidade === 'critico' && painel.alertas[a.chave] > 0,
  );

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8 grid gap-8 content-start grow">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
          Painel
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {new Date(painel.geradoEm).toLocaleString('pt-BR')}
          </span>
          <button
            type="button"
            className="btn btn-ghost !px-3 !py-1.5"
            disabled={ocupado}
            onClick={() => void recarregar()}
          >
            Atualizar
          </button>
        </div>
      </div>

      {mensagem ? (
        <p role="status" className="text-sm font-medium" style={{ color: 'var(--primary)' }}>
          {mensagem}
        </p>
      ) : null}

      {/* Incidente crítico ganha faixa própria: perdido numa grade de sete
          números, drift entre cache e ledger passa despercebido. */}
      {criticos.length > 0 ? (
        <section
          className="card p-5 grid gap-2"
          style={{ borderColor: 'var(--danger)', borderWidth: 2 }}
        >
          <h2 className="font-bold" style={{ color: 'var(--danger)' }}>
            Incidente crítico
          </h2>
          {criticos.map((a) => (
            <p key={a.chave} className="text-sm">
              <strong>
                {a.rotulo}: {painel.alertas[a.chave]}
              </strong>{' '}
              — {a.explica}
            </p>
          ))}
        </section>
      ) : null}

      <section className="grid gap-3">
        <h2 className="rotulo">Alertas</h2>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {ALERTAS.map((a) => {
            const valor = painel.alertas[a.chave];
            const aceso = valor > 0;
            return (
              <li
                key={a.chave}
                className="stat"
                title={a.explica}
                style={
                  aceso
                    ? {
                        borderColor: a.gravidade === 'critico' ? 'var(--danger)' : 'var(--primary)',
                      }
                    : undefined
                }
              >
                <div className="rotulo">{a.rotulo}</div>
                <div
                  className="stat-value"
                  style={aceso ? { color: a.gravidade === 'critico' ? 'var(--danger)' : 'var(--primary)' } : undefined}
                >
                  {valor}
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Hoje</h2>
        <ul className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
          <Painelzinho rotulo="Cobranças" valor={String(painel.funil.cobrancas_criadas)} />
          <Painelzinho rotulo="Pagas" valor={String(painel.funil.cobrancas_pagas)} />
          <Painelzinho rotulo="Depositado" valor={money(painel.funil.depositado_cents)} />
          <Painelzinho rotulo="Saques pedidos" valor={String(painel.funil.saques_pedidos)} />
          <Painelzinho rotulo="Saques na fila" valor={money(painel.funil.saques_na_fila_cents)} />
          <Painelzinho rotulo="Partidas" valor={String(painel.funil.partidas_criadas)} />
        </ul>
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Saques ({fila?.saques.length ?? 0})</h2>
        {!fila || fila.saques.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhum saque esperando decisão.
          </p>
        ) : (
          <ul className="grid gap-3">
            {fila.saques.map((saque) => (
              <LinhaSaque
                key={saque.id}
                saque={saque}
                onDecidir={decidir}
                onIdentidade={verificarIdentidade}
                ocupado={ocupado}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Partidas abertas ({painel.partidasAbertas.length})</h2>
        {painel.partidasAbertas.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhuma partida aberta. Nenhum dinheiro preso em escrow.
          </p>
        ) : (
          <ul className="grid gap-3">
            {painel.partidasAbertas.map((p) => (
              <LinhaPartida key={p.id} partida={p} onCancelar={cancelar} ocupado={ocupado} />
            ))}
          </ul>
        )}
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Faturamento</h2>
        {painel.faturamento.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhuma partida liquidada ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left">
                  <th className="rotulo py-2">Dia</th>
                  <th className="rotulo py-2">Modo</th>
                  <th className="rotulo py-2">Jogo</th>
                  <th className="rotulo py-2 text-right">Partidas</th>
                  <th className="rotulo py-2 text-right">Volume</th>
                  <th className="rotulo py-2 text-right">Rake</th>
                </tr>
              </thead>
              <tbody>
                {painel.faturamento.map((l, i) => (
                  <tr key={`${l.day}-${l.stake_mode}-${l.game_type}-${i}`} className="border-t">
                    <td className="py-2">{l.day}</td>
                    <td className="py-2">
                      {/* Rake virtual não é receita: existe só para medir o
                          beta, e somar com o real inventaria faturamento. */}
                      {l.stake_mode === 'REAL' ? 'real' : <span className="selo">virtual</span>}
                    </td>
                    <td className="py-2">{JOGO[l.game_type] ?? l.game_type}</td>
                    <td className="py-2 text-right">{l.matches}</td>
                    <td className="py-2 text-right money">{money(l.volume_cents)}</td>
                    <td className="py-2 text-right money">
                      {l.stake_mode === 'REAL' ? money(l.rake_cents) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Sinais de fraude</h2>
        <p className="text-sm text-muted-foreground -mt-1">
          Nenhum destes números pune sozinho. Cadência regular demais (coeficiente de variação perto
          de zero) acusa bot ingênuo; divergência de relógio acusa ferramenta no meio.
        </p>
        {painel.sinaisFraude.length === 0 ? (
          <p className="text-sm text-muted-foreground">Ninguém com lances suficientes ainda.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-left">
                  <th className="rotulo py-2">Jogador</th>
                  <th className="rotulo py-2 text-right">Lances</th>
                  <th className="rotulo py-2 text-right">Recusados</th>
                  <th className="rotulo py-2 text-right">Pensa (ms)</th>
                  <th className="rotulo py-2 text-right">Coef. variação</th>
                  <th className="rotulo py-2 text-right">Diverg. relógio</th>
                </tr>
              </thead>
              <tbody>
                {painel.sinaisFraude.map((s) => (
                  <tr key={s.user_id} className="border-t">
                    <td className="py-2">
                      <code className="text-xs">{curto(s.user_id)}</code>
                    </td>
                    <td className="py-2 text-right">{s.lances}</td>
                    <td className="py-2 text-right">{s.lances_recusados}</td>
                    <td className="py-2 text-right">{s.think_medio_ms ?? '—'}</td>
                    <td
                      className="py-2 text-right"
                      style={
                        s.coef_variacao !== null && s.coef_variacao < 0.15
                          ? { color: 'var(--danger)', fontWeight: 600 }
                          : undefined
                      }
                    >
                      {s.coef_variacao ?? '—'}
                    </td>
                    <td className="py-2 text-right">{s.divergencias_relogio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Pares suspeitos</h2>
        <p className="text-sm text-muted-foreground -mt-1">
          Dez ou mais confrontos entre as mesmas duas contas em 30 dias. Placar desequilibrado com
          volume alto é o padrão de chip dumping.
        </p>
        {painel.paresSuspeitos.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum par repetido o bastante.</p>
        ) : (
          <ul className="grid gap-2">
            {painel.paresSuspeitos.map((p) => (
              <li key={`${p.jogador_a}-${p.jogador_b}`} className="card p-3 text-sm flex flex-wrap gap-x-6 gap-y-1">
                <code className="text-xs">
                  {curto(p.jogador_a)} × {curto(p.jogador_b)}
                </code>
                <span>
                  {p.confrontos} confrontos, {p.vitorias_a} do primeiro
                </span>
                <span className="money">{money(p.volume_cents)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="grid gap-3">
        <h2 className="rotulo">Últimas ações administrativas</h2>
        {painel.ultimasAcoes.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nada registrado ainda.</p>
        ) : (
          <ul className="grid gap-1 text-sm">
            {painel.ultimasAcoes.map((a) => (
              <li key={`${a.created_at}-${a.target_id ?? ''}`} className="flex flex-wrap gap-x-3">
                <span className="text-muted-foreground">
                  {new Date(a.created_at).toLocaleString('pt-BR')}
                </span>
                <code className="text-xs">{curto(a.admin_id)}</code>
                <span>{a.action}</span>
                {a.target_id ? <code className="text-xs">{curto(a.target_id)}</code> : null}
                <span className="text-muted-foreground">{a.reason}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <button type="button" className="btn btn-ghost justify-self-start" onClick={onSair}>
        Voltar ao lobby
      </button>
    </main>
  );
}
